import {
  Box,
  Button,
  CircularProgress,
  OsModal,
  OsStack,
  Typography,
} from "@outschool/backpack";
import { Trans, useTranslation } from "@outschool/localization";
import { privacyPathCA } from "@outschool/routes";
import React, { useEffect } from "react";

import ExternalLink from "./ExternalLink";

interface LearnerConsentModalProps {
  open: boolean;
  onClose: () => void;
  learnerName: string;
  onSave?: (hasAgreed: boolean) => Promise<void> | void;
  shouldButtonSaySaveAndContinue?: boolean;
  shouldAllowSavingUnchecked?: boolean;
  defaultChecked?: boolean;
}

const MODAL_DESKTOP_WIDTH = 640;

export function LearnerMarketingConsentModal({
  open,
  onClose,
  learnerName,
  onSave,
}: LearnerConsentModalProps) {
  const { t } = useTranslation("ui-components-shared\\LearnerConsentModals");
  const [isSaving, setIsSaving] = React.useState(false);
  const [shouldClose, setShouldClose] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setIsSaving(false);
    }
  }, [open]);

  const handleResponse = React.useCallback(
    async (accepted: boolean) => {
      if (isSaving) {
        return;
      }
      setIsSaving(true);
      try {
        await onSave?.(accepted);
        setShouldClose(true);
      } finally {
        setIsSaving(false);
      }
    },
    [isSaving, onSave],
  );

  useEffect(() => {
    setShouldClose(false);
    if (shouldClose) {
      onClose();
    }
  }, [shouldClose, onClose]);

  return (
    <OsModal
      open={open}
      onClose={isSaving ? () => {} : onClose}
      modalDesktopWidth={MODAL_DESKTOP_WIDTH}
      title={t("Use {{learnerName}}\u2019s activity for personalization", {
        learnerName,
      })}
      actionSecondary={
        <Button
          variant="text"
          onClick={() => handleResponse(false)}
          disabled={isSaving}
        >
          {t("Reject")}
        </Button>
      }
      actionPrimary={
        <Button
          variant="contained"
          onClick={() => handleResponse(true)}
          disabled={isSaving}
          startIcon={
            isSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {t("Accept")}
        </Button>
      }
    >
      <OsStack direction="column" spacing={4}>
        <OsStack direction="column" spacing={2}>
          <Typography variant="body2" sx={{ color: "grey.700" }}>
            {t("For example:")}
          </Typography>
          <Box
            component="ul"
            sx={{
              margin: 0,
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 4,
              color: "grey.700",
            }}
          >
            <Typography variant="body2" component="li">
              {t("Viewing classes that {{learnerName}} has favorited", {
                learnerName,
              })}
            </Typography>
            <Typography variant="body2" component="li">
              {t(
                "Seeing recommendations for additional classes that {{learnerName}} might like based on what they\u2019ve viewed",
                { learnerName },
              )}
            </Typography>
          </Box>
        </OsStack>

        <Typography variant="body2" sx={{ color: "grey.700" }}>
          {t(
            "By clicking accept, I consent to Outschool sharing my learner information with third parties for marketing and advertising purposes. I understand that third parties may use the information for their own and others marketing, advertising, and other purposes, subject to the terms of their privacy notices.",
          )}
        </Typography>

        <OsStack direction="column" spacing={2}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {t("Why we\u2019re asking")}
          </Typography>
          <Typography variant="body2" sx={{ color: "grey.700" }}>
            {t(
              "Some U.S. state privacy laws require learners age 13 or older to consent to how their data is processed and used. Outschool provides this choice to all learners globally.",
            )}
          </Typography>
        </OsStack>

        <OsStack direction="column" spacing={2}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {t("Learn more or manage your choices")}
          </Typography>
          <Typography variant="body2" sx={{ color: "grey.700" }}>
            {t(
              "You can review these policies and change your preferences, including withdrawing consent, anytime.",
            )}
          </Typography>
        </OsStack>

        <OsStack direction="column" spacing={1}>
          <ExternalLink url="#">{t("Privacy Policy")}</ExternalLink>
          <ExternalLink url="#">
            {t("Consumer Health Data Privacy Notice")}
          </ExternalLink>
          <ExternalLink url="#">
            {t("Why we ask for learner consent")}
          </ExternalLink>
        </OsStack>
      </OsStack>
    </OsModal>
  );
}

export function LearnerHealthConsentModal({
  open,
  onClose,
  learnerName,
  onSave,
}: LearnerConsentModalProps) {
  const { t } = useTranslation("ui-components-shared\\LearnerConsentModals");
  const [isSaving, setIsSaving] = React.useState(false);
  const [shouldClose, setShouldClose] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setIsSaving(false);
    }
  }, [open]);

  const handleResponse = React.useCallback(
    async (accepted: boolean) => {
      if (isSaving) {
        return;
      }
      setIsSaving(true);
      try {
        await onSave?.(accepted);
        setShouldClose(true);
      } finally {
        setIsSaving(false);
      }
    },
    [isSaving, onSave],
  );

  useEffect(() => {
    setShouldClose(false);
    if (shouldClose) {
      onClose();
    }
  }, [shouldClose, onClose]);

  return (
    <OsModal
      open={open}
      onClose={isSaving ? () => {} : onClose}
      modalDesktopWidth={MODAL_DESKTOP_WIDTH}
      title={t(
        "Use {{learnerName}}\u2019s health information for personalization",
        { learnerName },
      )}
      actionSecondary={
        <Button
          variant="text"
          onClick={() => handleResponse(false)}
          disabled={isSaving}
        >
          {t("Reject")}
        </Button>
      }
      actionPrimary={
        <Button
          variant="contained"
          onClick={() => handleResponse(true)}
          disabled={isSaving}
          startIcon={
            isSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {t("Accept")}
        </Button>
      }
    >
      <OsStack direction="column" spacing={4}>
        <OsStack direction="column" spacing={2}>
          <Typography variant="body2" sx={{ color: "grey.700" }}>
            {t("For example:")}
          </Typography>
          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 20,
              display: "flex",
              flexDirection: "column",
              gap: 4,
              color: "grey.700",
            }}
          >
            <Typography variant="body2" component="li">
              {t(
                "Incorporating {{learnerName}}\u2019s learning needs into search results",
                {
                  learnerName,
                },
              )}
            </Typography>
            <Typography variant="body2" component="li">
              {t(
                "Displaying recommended classes based on {{learnerName}}\u2019s learning needs",
                { learnerName },
              )}
            </Typography>
          </Box>
        </OsStack>

        <Trans t={t}>
          <Typography variant="body2" sx={{ color: "grey.700" }}>
            I consent to Outschool collecting and processing my learner
            {"\u2019"}s health data as described in our{" "}
            <ExternalLink url={privacyPathCA()}>
              Consumer Health Data Privacy Notice
            </ExternalLink>
            . This includes information I provide about my learner{"\u2019"}s
            special learning needs related to their health-related information
            necessary to provide and personalize Outschool classes and services.
          </Typography>
        </Trans>

        <Typography variant="body2" sx={{ color: "grey.700" }}>
          {t(
            "To learn more or make changes to these preferences, including withdrawing consent, visit:",
          )}{" "}
          <ExternalLink url="#">{t("Privacy Policy")}</ExternalLink>.
        </Typography>
      </OsStack>
    </OsModal>
  );
}
